import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useTranslation } from "react-i18next";
import PageLayout from "components/layout/PageLayout";
import { useDeleteCompanyMutation, useGetAllCompaniesQuery } from "./api/platformAdminApiSlice";
import NewCompanyForm from "./components/NewCompanyForm";
import PlatformActivityStats from "./components/PlatformActivtyStats";
import PlatformStats from "./views/PlatformStats";
import durationToDays from "utility/SecondToDays";
import { Knob } from "primereact/knob";
import { useRef, useState } from "react";
import Avatar from "components/commons/Avatar";
import { useSelector } from "react-redux";
import { selectCompanyGuid } from "features/auth/authSlice";
import Modal from "components/commons/Modal";
import EntityFingerprint from "components/commons/EntityFingerprint";

function PlatformAdmin() {

    const [t] = useTranslation("global")
    const currentCompanyGuid = useSelector(selectCompanyGuid)
    const [expandedRows, setExpandedRows] = useState(null);
    const dt = useRef(null);
    const apiUrl = process.env.REACT_APP_API_URL

    // API
    const {
        data: companies,
        isFetching,
        isLoading,
        isSuccess,
        isError,
        error,
        refetch,
    } = useGetAllCompaniesQuery()

    const [
        deleteCompany, {
            isLoading: deleteCompanyIsLoading,
            isSuccess: deleteCompanyIsSuccess
        }
    ] = useDeleteCompanyMutation()



    let content

    const logoTemplate = (companyDetail) => {
        return companyDetail.company.img ? <img style={{
            maxHeight: 48,
            padding: 0
        }} src={`${apiUrl}/file?file=${companyDetail.company.img}`} alt="..." /> : null
    }

    const businessUnitTemplate = (companyDetail) => {
        return companyDetail.businessUnits.length
    }
    const employeesTemplate = (companyDetail) => {
        return <> {companyDetail.stakeholders.length}</>
    }

    const rolesTemplate = (companyDetail) => {
        return [...new Set(companyDetail.stakeholders.map(s => s.role.name))].length
    }

    const licenseTemplate = (companyDetail) => {
        return licenseStatusView(companyDetail.license)
    }

    const dayLeftTemplate = (companyDetail) => {
        let durationLeft = 0
        let durationTotal = 0
        let durationPerc = 0

        if (companyDetail.license.claims != null) {
            durationLeft = durationToDays(new Date(), companyDetail.license.claims.exp * 1000)
            durationTotal = durationToDays(companyDetail.license.claims.iat * 1000, companyDetail.license.claims.exp * 1000)
            durationPerc = Math.round((durationLeft / durationTotal) * 100)
        }

        return <Knob
            value={durationPerc}
            valueTemplate={`${durationLeft}`}
            valueColor={getKnobColor(durationPerc)}
            strokeWidth={8}
        />
    }

    const actionsTemplate = (companyDetail) => {
        return (
            <>
                <Modal
                    title= ""
                    modalId={"company_delete_" + companyDetail.company.guid}
                    modalLocked={companyDetail.company.guid == currentCompanyGuid}
                    buttonClass="btn btn-danger"
                    buttonIcon={<i className="fa-solid fa-trash"></i>}
                    buttonLabel={t("action.delete")}
                    iconOnly={true}
                    body={t("helper.generic_entity_deleting")}
                    onConfirm={() => deleteCompany(companyDetail.company.guid)}
                />

            </>
        )
    }

    const getDayLeft = (companyDetail) => {
        if (companyDetail.license.claims != null) {
            return durationToDays(new Date(), companyDetail.license.claims.exp * 1000)
        } else return 0
    }

    const getKnobColor = (value) => {
        if (value > 75) {
            return "#2bb673"
        } else if (value >= 50) {
            return "green"
        } else if (value > 20) {
            return "orange"
        } else return "red"
    }

    const licenseStatusView = (licenseInfo) => {

        switch (licenseInfo.status) {
            case "VALID":
                return <span className="badge bg-success"> <i className="fa-solid fa-circle-check"></i>  Valid </span>
                break;
            case "EXPIRED":
                return <span className="badge bg-warning text-dark"> <i className="fa-regular fa-circle-xmark"></i>  Expired </span>
                break;
            default: return <span className="badge bg-danger"> <i className="fa-solid fa-circle-check"></i>  Invalid </span>
        }
    }

    const rowExpansionTemplate = (companyDetail) => {
        return <div className="row">
            <div className="col">
                <div className="card bg-black bg-opacity-10 text-white mb-4 text-center">
                    <div className="card-body">
                        <p className="text-accent">{t("property.administrators")}:</p>
                        {
                            companyDetail.stakeholders.filter(u => u.role.isCritical).map(admin =>
                                <Avatar displayName={admin.user.displayName} guid={admin.user.guid} key={admin.user.guid} />
                            )
                        }
                    </div>
                    <div className="card-footer">
                        <EntityFingerprint guid={companyDetail.company.guid} extended={true}/>
                    </div>
                </div>
            </div>
            <div className="col">
                <div className="col card bg-black bg-opacity-10 text-white mb-4 text-center">
                    <div className="card-body">
                        <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3">
                            <div className="col p-2">
                                {t("entity.users")}<br />
                                <h2 className="p-2"><i className="fas fa-users"></i> {employeesTemplate(companyDetail)}</h2>
                            </div>
                            <div className="col p-2">
                                {t("entity.roles")}<br />
                                <h2 className="p-2"><i className="fa-solid fa-user-shield" /> {rolesTemplate(companyDetail)}</h2>
                            </div>
                            <div className="col p-2">
                                {t("entity.businessUnits")}<br />
                                <h2 className="p-2"><i className="fa-solid fa-building-flag"></i> {businessUnitTemplate(companyDetail)}</h2>
                            </div>
                        </div>
                    </div>
                </div>
                <table className="table table-hover ">

                    <tbody className=" ">
                        <tr>
                            <th scope="row"  >Status</th>
                            <td >
                                <h6>{licenseStatusView(companyDetail.license)}</h6>
                            </td>
                        </tr>
                        {
                            companyDetail.license.claims &&
                            <>
                                <tr>
                                    <th scope="row">Creation</th>
                                    <td>{new Date(companyDetail.license.claims.iat * 1000).toLocaleString()}</td>
                                </tr>
                                <tr>
                                    <th scope="row">Expiration</th>
                                    <td>{new Date(companyDetail.license.claims.exp * 1000).toLocaleString()}</td>
                                </tr>
                                <tr>
                                    <th scope="row">Users</th>
                                    <td>{companyDetail.license.claims.maxUsers}</td>
                                </tr>
                                <tr>
                                    <th scope="row">Modules</th>
                                    <td>
                                        {
                                            companyDetail.license.claims.modules.map(module => {
                                                return <><span className="badge bg-dark">{module}</span><br /></>
                                            })

                                        }

                                    </td>
                                </tr>
                            </>
                        }

                    </tbody>
                </table>
            </div>

        </div>
    }



    if (isSuccess) {
        content = <>
            <PlatformStats />
            <PlatformActivityStats />
            <div className="card bg-dark text-white mb-4">
                <div className="card-header">
                    <i className="fa-solid fa-landmark"></i>  {t("entity.companyWithCount", { count: companies.length })}

                </div>

                <div className="card-body">
                    <div className="col mb-2 text-end">
                        <NewCompanyForm />
                    </div>
                    <DataTable
                        value={companies}
                        stripedRows
                        tableStyle={{ minWidth: '50rem' }}
                        paginator rows={25}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        dataKey="company.guid"
                        expandedRows={expandedRows}
                        onRowToggle={(e) => setExpandedRows(e.data)}
                        rowExpansionTemplate={rowExpansionTemplate}
                        ref={dt}
                    >
                        <Column expander={() => { return true }} style={{ width: '5rem' }} />

                        <Column field="company.name" header={t("property.name")} sortable></Column>
                        <Column body={logoTemplate}   ></Column>

                        <Column field="company.vat" header={t("property.vat")} ></Column>
                        <Column body={licenseTemplate} header={"License"} ></Column>
                        <Column body={dayLeftTemplate} header={"Day Left"} ></Column>
                        <Column body={actionsTemplate} header={t("property.actions")}></Column>
                    </DataTable>
                </div>
            </div >
        </>

    }


    return <PageLayout
        pageTitle={"Platform Admin"}
        bodyContent={content}
        onPageRefresh={refetch}
        isError={isError}
        errorMsg={error}
        isLoadingPage={isLoading}
        isLoadingContent={isFetching}
    />

}


export default PlatformAdmin